import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./WhatIveDone.css";
import { whatIveDoneApi } from "./whatIveDoneApi";
import { BarChart } from "@mui/x-charts/BarChart";
import {
  FiActivity, FiCode, FiCheckSquare, FiUploadCloud, FiLayers, FiInbox, FiUsers,
  FiChevronDown, FiChevronRight, FiRefreshCw, FiCalendar, FiClock,
  FiAlertTriangle, FiBell, FiLifeBuoy, FiCheckCircle,
} from "react-icons/fi";
import { GoBug } from "react-icons/go";

/* ───── Date helpers ───── */
const toStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayStr = () => toStr(new Date());

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "custom", label: "Custom" },
];

/* ───── Per-mode display config ───── */
const MODE_CONFIG = {
  dev: {
    subtitle: "tickets you moved, grouped by sprint",
    chartTitle: "Moves per Sprint",
    kpis: [
      { key: "in_dev", label: "Moved to Dev", cls: "dev", Icon: FiCode },
      { key: "in_qa", label: "Moved to QA", cls: "qa", Icon: FiCheckSquare },
      { key: "production_moved", label: "Moved to Production", cls: "prod", Icon: FiUploadCloud },
      { key: "total", label: "Total Moves", cls: "total", Icon: FiActivity },
      { key: "errors_to_production", label: "Errors → Production", cls: "bug", Icon: FiAlertTriangle },
      { key: "reminders_assigned", label: "Tasks / Reminders", cls: "peer", Icon: FiBell },
      { key: "cs_assigned", label: "Support: Assigned", cls: "cs", Icon: FiLifeBuoy },
      { key: "cs_completed", label: "Support: Completed", cls: "prod", Icon: FiCheckCircle },
    ],
    pills: [
      { key: "in_dev", label: "Dev", cls: "dev" },
      { key: "in_qa", label: "QA", cls: "qa" },
      { key: "production_moved", label: "Prod", cls: "prod" },
      { key: "total", label: "total", cls: "total", suffix: true },
    ],
    chart: [
      { key: "in_dev", label: "Dev", color: "#ff9800" },
      { key: "in_qa", label: "QA", color: "#ffc107" },
      { key: "production_moved", label: "Production", color: "#4caf50" },
    ],
    activityKeys: ["in_dev", "in_qa", "production_moved", "errors_to_production",
      "reminders_assigned", "cs_assigned", "cs_completed"],
  },
  qa: {
    subtitle: "your QA activity, grouped by sprint",
    chartTitle: "Activity per Sprint",
    kpis: [
      { key: "assigned_qa", label: "Assigned as QA", cls: "total", Icon: FiInbox },
      { key: "assigned_peer_qa", label: "Assigned as Peer QA", cls: "peer", Icon: FiUsers },
      { key: "qa_passed", label: "QA Passed / UAT", cls: "qa", Icon: FiCheckSquare },
      { key: "production_moved", label: "Moved to Production", cls: "prod", Icon: FiUploadCloud },
      { key: "bugs_raised", label: "Bugs Raised", cls: "bug", Icon: GoBug },
    ],
    pills: [
      { key: "assigned_qa", label: "QA", cls: "total" },
      { key: "assigned_peer_qa", label: "Peer", cls: "peer" },
      { key: "qa_passed", label: "Pass", cls: "qa" },
      { key: "production_moved", label: "Prod", cls: "prod" },
      { key: "bugs_raised", label: "Bugs", cls: "bug" },
    ],
    chart: [
      { key: "qa_passed", label: "QA Passed", color: "#4caf50" },
      { key: "production_moved", label: "Production", color: "#2196f3" },
      { key: "bugs_raised", label: "Bugs", color: "#f44336" },
    ],
    activityKeys: ["assigned_qa", "assigned_peer_qa", "qa_passed", "production_moved", "bugs_raised"],
  },
};

const getMyName = () => {
  try {
    const login = JSON.parse(localStorage.getItem("loginData") || "{}");
    return login?.data?.user?.member?.name || "";
  } catch {
    return "";
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const WhatIveDone = () => {
  const [rangeMode, setRangeMode] = useState("today");
  const [customFrom, setCustomFrom] = useState(todayStr());
  const [customTo, setCustomTo] = useState(todayStr());
  const [selectedSprintIds, setSelectedSprintIds] = useState([]);
  const [sprintOpen, setSprintOpen] = useState(false);

  const [data, setData] = useState(null);
  const [availableSprints, setAvailableSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});

  const sprintBoxRef = useRef(null);
  const myName = useMemo(getMyName, []);

  useEffect(() => {
    const onClick = (e) => {
      if (sprintBoxRef.current && !sprintBoxRef.current.contains(e.target)) {
        setSprintOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const buildParams = useCallback(() => {
    const params = { sprint_ids: selectedSprintIds };
    if (rangeMode === "today" || rangeMode === "week") {
      params.range = rangeMode;
    } else if (rangeMode === "custom") {
      params.from_date = customFrom;
      params.to_date = customTo;
    }
    return params;
  }, [rangeMode, customFrom, customTo, selectedSprintIds]);

  const fetchData = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError("");
    try {
      const res = await whatIveDoneApi.getMyWork(buildParams());
      setData(res.data || null);
      if (res.data?.available_sprints?.length) {
        setAvailableSprints(res.data.available_sprints);
      }
    } catch (e) {
      setError(e.message || "Something went wrong");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSprint = (id) => {
    const sid = String(id);
    setSelectedSprintIds((prev) =>
      prev.includes(sid) ? prev.filter((v) => v !== sid) : [...prev, sid]
    );
  };
  const toggleExpand = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const mode = data?.mode === "qa" ? "qa" : "dev";
  const cfg = MODE_CONFIG[mode];
  const summary = useMemo(() => data?.summary || {}, [data]);
  const sprints = useMemo(() => data?.sprints || [], [data]);

  const totalActivity = useMemo(
    () => cfg.activityKeys.reduce((sum, k) => sum + (summary[k] || 0), 0),
    [cfg, summary]
  );

  const chart = useMemo(() => {
    const withActivity = sprints.filter(
      (s) => cfg.chart.reduce((sum, c) => sum + (s[c.key] || 0), 0) > 0
    );
    return {
      labels: withActivity.map((s) => s.sprint_name),
      series: cfg.chart.map((c) => ({
        data: withActivity.map((s) => s[c.key] || 0),
        label: c.label,
        color: c.color,
        stack: "s",
      })),
      has: withActivity.length > 0,
    };
  }, [sprints, cfg]);

  const sprintFilterLabel =
    selectedSprintIds.length === 0
      ? "All Sprints"
      : `${selectedSprintIds.length} Sprint${selectedSprintIds.length > 1 ? "s" : ""}`;

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="mw">
      <div className="mw-topbar">
        <div>
          <h1 className="mw-title">
            <FiActivity size={20} /> What I've Done
            {mode === "qa" && <span className="mw-rolebadge">QA</span>}
          </h1>
          <p className="mw-subtitle">
            {myName ? `${myName} — ` : ""}{cfg.subtitle}
          </p>
        </div>
        <button
          className={`mw-refresh ${refreshing ? "mw-refresh--spin" : ""}`}
          onClick={() => fetchData(true)}
          title="Refresh"
        >
          <FiRefreshCw size={14} />
        </button>
      </div>

      {/* Filter bar */}
      <div className="mw-filters">
        <div className="mw-presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={`mw-preset ${rangeMode === p.key ? "mw-preset--active" : ""}`}
              onClick={() => setRangeMode(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {rangeMode === "custom" && (
          <div className="mw-daterange">
            <span className="mw-daterange__ico"><FiCalendar size={13} /></span>
            <input type="date" value={customFrom} max={customTo || todayStr()}
              onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="mw-daterange__sep">to</span>
            <input type="date" value={customTo} min={customFrom} max={todayStr()}
              onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}

        <div className="mw-sprintfilter" ref={sprintBoxRef}>
          <button
            className={`mw-sprintfilter__btn ${selectedSprintIds.length ? "mw-sprintfilter__btn--active" : ""}`}
            onClick={() => setSprintOpen((o) => !o)}
          >
            <FiLayers size={13} /> {sprintFilterLabel}
            <FiChevronDown size={13} />
          </button>
          {sprintOpen && (
            <div className="mw-dropdown" onClick={(e) => e.stopPropagation()}>
              {availableSprints.length === 0 && (
                <div className="mw-dropdown__empty">No sprints with activity</div>
              )}
              {selectedSprintIds.length > 0 && (
                <div className="mw-dropdown__clear" onClick={() => setSelectedSprintIds([])}>
                  Clear selection
                </div>
              )}
              {availableSprints.map((s) => {
                const sid = String(s.id);
                const checked = selectedSprintIds.includes(sid);
                return (
                  <label key={sid} className={`mw-opt ${checked ? "mw-opt--on" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleSprint(sid)} />
                    <span>{s.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="mw-loading">
          <div className="mw-spinner" />
          <span>Loading your work…</span>
        </div>
      ) : error ? (
        <div className="mw-empty mw-empty--error">
          <FiActivity size={38} />
          <h3>Couldn't load your work</h3>
          <p>{error}</p>
          <button className="mw-preset" onClick={() => fetchData()}>Retry</button>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="mw-kpis">
            {cfg.kpis.map(({ key, label, cls, Icon }) => (
              <div className={`mw-kpi mw-kpi--${cls}`} key={key}>
                <div className="mw-kpi__icon"><Icon size={18} /></div>
                <div className="mw-kpi__body">
                  <span className="mw-kpi__value">{summary[key] || 0}</span>
                  <span className="mw-kpi__label">{label}</span>
                </div>
              </div>
            ))}
          </div>

          {totalActivity === 0 ? (
            <div className="mw-empty">
              <FiClock size={38} />
              <h3>Nothing here yet</h3>
              <p>No activity recorded for the selected period. Try a wider date range or a different sprint.</p>
            </div>
          ) : (
            <>
              {chart.has && (
                <div className="mw-card mw-chart">
                  <div className="mw-card__header">
                    <h3><FiLayers size={14} /> {cfg.chartTitle}</h3>
                  </div>
                  <BarChart
                    height={280}
                    xAxis={[{ scaleType: "band", data: chart.labels }]}
                    series={chart.series}
                    slotProps={{ legend: { direction: "row", position: { vertical: "top", horizontal: "middle" } } }}
                    sx={{
                      "& .MuiChartsAxis-tickLabel": { fill: "var(--text-muted)" },
                      "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "var(--border-subtle)" },
                      "& .MuiChartsLegend-series text": { fill: "var(--text-primary) !important" },
                    }}
                  />
                </div>
              )}

              <div className="mw-sprints">
                {sprints.map((s) => {
                  const key = s.sprint_id == null ? "no-sprint" : String(s.sprint_id);
                  const open = !!expanded[key];
                  const hasTickets = (s.tickets || []).length > 0;
                  return (
                    <div className="mw-card mw-sprintcard" key={key}>
                      <div
                        className={`mw-sprintcard__head ${hasTickets ? "" : "mw-sprintcard__head--static"}`}
                        onClick={() => hasTickets && toggleExpand(key)}
                      >
                        <span className="mw-sprintcard__chevron">
                          {hasTickets
                            ? (open ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />)
                            : <FiChevronRight size={16} style={{ opacity: 0.25 }} />}
                        </span>
                        <span className="mw-sprintcard__name">{s.sprint_name}</span>
                        <div className="mw-sprintcard__stats">
                          {cfg.pills.map(({ key: k, label, cls, suffix }) => (
                            <span className={`mw-pill mw-pill--${cls}`} key={k}>
                              {suffix ? `${s[k] || 0} ${label}` : `${label} ${s[k] || 0}`}
                            </span>
                          ))}
                        </div>
                      </div>

                      {open && hasTickets && (
                        <div className="mw-tickets">
                          <table>
                            <thead>
                              <tr>
                                <th>Ticket</th>
                                <th>Description</th>
                                <th>Move</th>
                                <th>When</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.tickets.map((t, i) => (
                                <tr key={`${t.ticket_id}-${i}`}>
                                  <td className="mw-tickets__code">{t.ticket_code}</td>
                                  <td className="mw-tickets__desc" title={t.short_description}>
                                    {t.short_description || "—"}
                                  </td>
                                  <td className="mw-tickets__move">
                                    <span className="mw-move-from">{t.from_status || "New"}</span>
                                    <FiChevronRight size={11} />
                                    <span className={`mw-move-to mw-move-to--${t.stage || "other"}`}>
                                      {t.to_status || "—"}
                                    </span>
                                  </td>
                                  <td className="mw-tickets__when">{formatDateTime(t.changed_date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WhatIveDone;
